/**
 * ProShip Direct v2 — compact stat styles.
 *
 * Two reasons this exists rather than using patterns/kpiCardSx:
 *
 * 1. Those helpers are FUNCTIONS — `kpiCardSx(accent)` and `kpiValueSx(color)`. Passing them to
 *    `sx` uncalled makes MUI invoke them with the theme, which silently discards the colour and
 *    leaves a 28px value. Easy mistake, made once already.
 * 2. Even called correctly they are dashboard-scale. This module puts four to nine cards in a
 *    row above dense tables, where 28px numerals dominate the screen.
 *
 * Objects, not functions, so `sx={statCardSx}` is correct by construction.
 */
import { BORDER, WHITE, SUBTLE, NAVY, LIGHT_BG } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { RADIUS } from '../../../styles/foundation/spacing';

export const statCardSx = {
  p: 1.5,
  borderRadius: RADIUS.md,
  border: `1px solid ${BORDER}`,
  bgcolor: WHITE,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

/** Muted variant, for cards that report a count you hope is zero (blocked pincodes). */
export const statCardMutedSx = { ...statCardSx, bgcolor: LIGHT_BG };

export const statLabelSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['4xs'],
  color: SUBTLE,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  lineHeight: 1.3,
};

export const statValueSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: SIZE.xl,
  color: NAVY,
  lineHeight: 1.25,
  fontVariantNumeric: 'tabular-nums',
};

export const statSubSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.regular,
  fontSize: SIZE['3xs'],
  color: SUBTLE,
  lineHeight: 1.35,
};

/**
 * Compact table cells. patterns/headerCellSx and cellSx are 13px bold and 15px — right for a
 * full-width dashboard table, too heavy for the dense reference tables and modals in this
 * module, where the table is supporting detail rather than the subject of the screen.
 */
export const tHeadSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['4xs'],
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: SUBTLE,
  bgcolor: LIGHT_BG,
  borderBottom: `1px solid ${BORDER}`,
  py: 0.85,
  px: 1.25,
  whiteSpace: 'nowrap',
};

export const tCellSx = {
  fontFamily: FONT,
  whiteSpace: 'nowrap',
  fontSize: SIZE['2xs'],
  color: NAVY,
  py: 0.7,
  px: 1.25,
  borderBottom: `1px solid ${BORDER}`,
};

/** Numeric variant — aligns digits in a column. */
export const tNumSx = { ...tCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
